import FormComponent from '../../../../components/FormComponent';
import SelectorEngine from '../../../../utils/dom/selector-engine';
import EventHandler from '../../../../utils/dom/event-handler';

class PackageCardQuestion extends FormComponent {
	constructor(containerElement, config) {
		super(containerElement, config);
		this.initOptions();
		// Defer so FormGenerator can attach onChange before we publish the default answer
		queueMicrotask(() => this.publishCurrentValue());
	}

	initOptions() {
		const options = this.data?.options || [];
		if (options.length <= 1) {
			const singleOption = options[0] || {};
			this._value = singleOption.id || this.data?.value || 'standard-free';
			return;
		}

		const selectedOption =
			options.find((opt) => opt.id === this.data?.value) ||
			options.find((opt) => opt.isDefault || opt.isDefault);
		this._value = selectedOption?.id || this.data?.value || '';
	}

	publishCurrentValue() {
		const currentValue = this.getValue();
		if (currentValue?.value) {
			this.afterValueChange(currentValue);
		}
	}

	getFieldName() {
		return this.data?.fieldName || this.data?.fieldName || 'benefitsPackage';
	}

	markup(_value, data, titleMarkup = '') {
		const spacingClass = data?.spacingClass || data?.spacingClass || 'u-mb-4';
		const options = data?.options || [];
		const cardClasses = 'lta-package-card--form';

		// Case 1: Single package -> card only, no radios
		if (options.length <= 1) {
			const singleOption = options[0] || {};
			const optionId = singleOption.id || data?.value || 'standard-free';
			this._value = optionId;

			const packageType =
				singleOption.packageType ||
				singleOption.packageType ||
				data?.packageType ||
				data?.packageType ||
				'accent-yellow';
			const packageTitle = singleOption.title || singleOption.label || data?.packageTitle || 'Standard';
			const primaryText =
				singleOption.primaryText ||
				singleOption.description ||
				data?.packageDescription ||
				data?.primaryText ||
				'Explanation of the package...';
			const listItems =
				singleOption.listItems ||
				singleOption.listItems ||
				data?.listItems ||
				data?.listItems ||
				['Example benefit', 'Example benefit'];
			const listItemsJSON = JSON.stringify(listItems).replace(/'/g, '&apos;');

			return `
				<div class="lta-package-card-question ${spacingClass}">
					${titleMarkup}
					<div class="u-mb-4">
						<lta-package-card
							type="${packageType}"
							title="${packageTitle}"
							primary-text="${primaryText}"
							list-items='${listItemsJSON}'
							classes="${cardClasses}"
							full
						></lta-package-card>
					</div>
				</div>
			`;
		}

		// Case 2: Multiple packages -> radio + card, matching other form questions
		const questionId = data.id || 'package_question';
		const selectedValue =
			this._value || options.find((opt) => opt.isDefault || opt.isDefault)?.id || '';
		const optionRows = options
			.map((option, index) => {
				const optionId = option.id || `option_${index}`;
				const isChecked = selectedValue === optionId;
				const packageType =
					option.packageType || option.packageType || (index === 0 ? 'accent-yellow' : 'accent-pink');
				const packageTitle = option.title || option.label || (index === 0 ? 'Premium' : 'Standard');
				const primaryText = option.primaryText || option.description || 'Explanation of the package...';
				const listItems =
					option.listItems ||
					option.listItems ||
					(index === 0
						? ['Insurance included', 'Example benefit', 'Example benefit', 'Example benefit']
						: ['Example benefit', 'Example benefit', 'Example benefit', 'Example benefit']);
				const listItemsJSON = JSON.stringify(listItems).replace(/'/g, '&apos;');

				return `
					<div class="lta-package-card-option-row">
						<input
							type="radio"
							class="lta-form-check-input js-package-card-radio"
							id="package_radio_${questionId}_${optionId}"
							name="benefitsPackageRadio_${questionId}"
							value="${optionId}"
							${isChecked ? 'checked' : ''}
						/>
						<label class="lta-package-card-option-row__label" for="package_radio_${questionId}_${optionId}">
							<lta-package-card
								type="${packageType}"
								title="${packageTitle}"
								primary-text="${primaryText}"
								list-items='${listItemsJSON}'
								classes="${cardClasses}"
								full
							></lta-package-card>
						</label>
					</div>
				`;
			})
			.join('');

		return `
			<div class="lta-package-card-question ${spacingClass}">
				${titleMarkup}
				<div class="lta-package-card-options-group">
					${optionRows}
				</div>
			</div>
		`;
	}

	afterRender() {
		this.initEventListeners();
	}

	initEventListeners() {
		const radioInputs = SelectorEngine.find('.js-package-card-radio', this.containerEl);

		for (const radio of radioInputs) {
			EventHandler.on(radio, 'change', (event) => {
				const val = event.target.value;
				this._value = val;
				this.afterValueChange({
					fieldName: this.getFieldName(),
					value: val
				});
			});
		}
	}

	getValue() {
		const options = this.data?.options || [];
		let val = this._value;
		if (options.length <= 1) {
			const singleOption = options[0] || {};
			val = singleOption.id || this._value || 'standard-free';
		} else if (!val) {
			val = options.find((opt) => opt.isDefault || opt.isDefault)?.id || '';
		}
		return {
			fieldName: this.getFieldName(),
			value: val
		};
	}

	getValueFromElement() {
		return this.getValue();
	}

	validate() {
		const answer = this.getValue();
		if (this.data?.isOptional) {
			return true;
		}
		return Boolean(answer?.value);
	}
}

export default PackageCardQuestion;
